import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { paymentsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CreditCard,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

const formatAmount = (amount, currency = 'KZT') => {
  if (!amount) return '0';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency === 'KZT' ? 'KZT' : currency,
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('ru-RU').format(num || 0);
};

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'from-emerald-500 to-cyan-500 shadow-emerald-500/25',
    blue: 'from-blue-500 to-indigo-500 shadow-blue-500/25',
    purple: 'from-purple-500 to-pink-500 shadow-purple-500/25',
    amber: 'from-amber-500 to-orange-500 shadow-amber-500/25',
    red: 'from-red-500 to-rose-500 shadow-red-500/25',
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-all duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(trend)}% vs вчера
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBadge = ({ status, t }) => {
  const statusLabels = {
    approved: t ? t('payments.approved') : 'Approved',
    declined: t ? t('payments.declined') : 'Declined', 
    pending: t ? t('payments.processing') : 'Processing',
    refunded: t ? t('payments.refunded') : 'Refunded',
    cancelled: t ? t('common.cancel') : 'Cancelled',
    init: 'init',
    expired: 'expired',
  };
  
  const statusStyles = {
    approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    declined: 'bg-red-500/20 text-red-400 border-red-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    refunded: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    cancelled: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    init: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    expired: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  const label = statusLabels[status] || status || 'N/A';
  const className = statusStyles[status] || statusStyles.pending;
  return <Badge variant="outline" className={className}>{label}</Badge>;
};

const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentsAPI.dashboard();
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки данных');
      // Set mock data for demo
      setStats({
        today: { total_count: 0, approved_count: 0, declined_count: 0, pending_count: 0, total_amount: 0, approved_amount: 0, success_rate: 0, currency: 'KZT' },
        week: { total_count: 0, approved_count: 0, declined_count: 0, pending_count: 0, total_amount: 0, approved_amount: 0, success_rate: 0, currency: 'KZT' },
        month: { total_count: 0, approved_count: 0, declined_count: 0, pending_count: 0, total_amount: 0, approved_amount: 0, success_rate: 0, currency: 'KZT' },
        recent_payments: [],
        top_merchants: [],
        hourly_stats: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('dashboard.title')}</h1>
          <p className="text-slate-400 mt-1">{t('auth.welcome')}, {user?.name}</p>
        </div>
        <Button
          onClick={fetchDashboard}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {t("common.refresh")}
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
          {error} - Отображаются демо-данные. Подключите базу данных OxyPay.
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('dashboard.todayAmount')}
          value={formatAmount(stats?.today?.approved_amount, stats?.today?.currency || 'RUB')}
          subtitle={`${formatNumber(stats?.today?.approved_count)} успешных`}
          icon={DollarSign}
          color="emerald"
        />
        <StatCard
          title={t('dashboard.totalPayments')}
          value={formatNumber(stats?.today?.total_count)}
          subtitle="За сегодня"
          icon={CreditCard}
          color="blue"
        />
        <StatCard
          title={t('dashboard.conversion')}
          value={`${stats?.today?.success_rate || 0}%`}
          subtitle={t('dashboard.successful')}
          icon={Activity}
          color="purple"
        />
        <StatCard
          title={t('dashboard.totalAmount')}
          value={formatAmount(stats?.month?.approved_amount, stats?.month?.currency || 'RUB')}
          subtitle={`за 30 дней • за 7 дней: ${formatAmount(stats?.week?.approved_amount, stats?.week?.currency || 'RUB')}`}
          icon={BarChart3}
          color="amber"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              {t("dashboard.successful")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-400">{formatNumber(stats?.today?.approved_count)}</p>
            <p className="text-xs text-slate-500 mt-1">{t('aml.alertsToday').toLowerCase()}</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              {t("dashboard.failed")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-400">{formatNumber(stats?.today?.declined_count)}</p>
            <p className="text-xs text-slate-500 mt-1">{t('aml.alertsToday').toLowerCase()}</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-400 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              {t("dashboard.pending")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-amber-400">{formatNumber(stats?.today?.pending_count)}</p>
            <p className="text-xs text-slate-500 mt-1">{t('aml.alertsToday').toLowerCase()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Payments & Top Merchants */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Payments */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              {t("payments.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_payments?.length > 0 ? (
                stats.recent_payments.slice(0, 5).map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-slate-300" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">#{payment.id}</p>
                        <p className="text-xs text-slate-400">{payment.card_masked_number || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-white">
                        {formatAmount(payment.amount, payment.currency)}
                      </p>
                      <StatusBadge status={payment.status} t={t} />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">Нет данных о платежах</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Merchants */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              {t("merchants.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.top_merchants?.length > 0 ? (
                stats.top_merchants.slice(0, 5).map((merchant, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{merchant.merchant_key}</p>
                        <p className="text-xs text-slate-400">{formatNumber(merchant.count)} транзакций</p>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-emerald-400">
                      {formatAmount(merchant.total_amount, merchant.currency || 'KZT')}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">{t('common.noData')}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
